import { useQuery } from '@apollo/client';
import { Size } from '@busybusy/webapp-react-ui';
import { MEMBERS_QUERY } from 'apollo/queries/member-queries';
import classNames from 'classnames';
import EmployeeCheckList from 'components/domain/member/EmployeeCheckList/EmployeeCheckList';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import ImageDrawer, { IImageDrawerProps, IImagePayload } from 'components/foundation/ImageDrawer/ImageDrawer';
import { useOpennable } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMember } from 'types';
import IMemberPermissionType from 'types/MemberPermissionType';

export interface IEmployeeImageDrawerProps extends Omit<IImageDrawerProps, 'images' | 'onButtonClick'> {
  values: string[];
  editable: boolean;
  permissions?: IMemberPermissionType;
  onChange?: (values: string[]) => void;
  className?: string | object;
}

const EmployeeImageDrawer = (props: IEmployeeImageDrawerProps) => {
  const { values, editable, error, onChange, className, ...drawerProps } = props;

  const [t] = useTranslation();

  const [imagePayloads, setImagePayloads] = useState<IImagePayload[]>([]);
  const checkListDetails = useOpennable();
  const { data } = useQuery<{ members: IMember[] }>(MEMBERS_QUERY, {
    variables: {
      filter: {
        id: { contains: values },
      },
      sort: [{ firstName: 'asc' }, { lastName: 'asc' }],
    },
  });

  useEffect(() => {
    if (data) {
      setImagePayloads(
        data.members
          .filter((member) => values.some((value) => value === member.id))
          .map((member) => {
            return {
              firstName: member.firstName ? member.firstName : '',
              lastName: member.lastName,
              image: member.imageUrl,
            };
          })
      );
    }
  }, [data, values]);

  function onCheckSubmit(checked: string[]) {
    if (onChange) {
      onChange(checked);
    }
    checkListDetails.close();
  }

  const classes = classNames(
    {
      'member-image-drawer': true,
    },
    className
  );

  return (
    <>
      <HeaderDialog
        title={t('Select Employees')}
        divider={false}
        isOpen={checkListDetails.isOpen}
        onClose={checkListDetails.close}
      >
        <EmployeeCheckList enableGroups={true} enableSearch={true} onSubmit={onCheckSubmit} checkedItems={values} />
      </HeaderDialog>
      <ImageDrawer
        className={classes}
        onButtonClick={editable ? checkListDetails.open : undefined}
        images={imagePayloads}
        {...drawerProps}
      />
    </>
  );
};

EmployeeImageDrawer.defaultProps = {
  ...ImageDrawer.defaultProps,
  numInitials: 2 as 1 | 2 | undefined,
  editable: true,
  size: Size.MEDIUM,
};

export default EmployeeImageDrawer;
